import { useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  Users,
  Package,
  DollarSign,
  AlertCircle,
  Clock,
  CheckCircle,
  Printer,
  Filter,
  UserPlus,
  Building2,
  Phone,
  MapPin,
  Calendar,
  Loader2
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { toast } from 'sonner';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import api from '../../api/clients';
import { formatCurrency, formatDate, getFileUrl } from '../../lib/utils';
import { HistorialProductos } from './HistorialProductos';
import { exportarVentasPDF } from '../../api/reports';
import { useProducts } from '../../hooks/useProducts';

interface ReportSummary {
  totalVentas: number;
  totalProductos: number;
  totalClientes: number;
  totalProveedores: number;
  ventasHoy: number;
  ventasMes: number;
  stockBajo: number;
}

interface VentaItem {
  id: number;
  clienteNombre: string;
  total: number;
  fechaVenta: string;
  estado: string;
}

interface ProductoItem {
  id: number;
  nombre: string;
  stock: number;
  precio: number;
  categoria: string;
}

interface ContactoReciente {
  id: number;
  nombre: string;
  tipo: 'cliente' | 'proveedor';
  telefono?: string;
  localidad?: string;
  fecha: string;
}

export function Reports() {
  const [activeReport, setActiveReport] = useState<'dashboard' | 'ventas' | 'stock' | 'historial'>('dashboard');
  
  const [dateRange, setDateRange] = useState({
    inicio: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    fin: new Date().toISOString().split('T')[0],
  });

  const [tempDateRange, setTempDateRange] = useState({ ...dateRange });
  const [isExportingPDF, setIsExportingPDF] = useState(false);

  // ✅ Obtener TODOS los productos para usar en la pestaña Stock
  const { data: allProducts, isLoading: productsLoading } = useProducts();

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['reports', 'summary'],
    queryFn: async () => {
      const res = await api.get('/reports/summary');
      return res.data.data as ReportSummary;
    },
    refetchInterval: 4000,
    enabled: activeReport === 'dashboard',
  });

  const { data: ventasRecientes, isLoading: ventasLoading } = useQuery({
    queryKey: ['reports', 'ventas', dateRange],
    queryFn: async () => {
      const res = await api.get('/reports/ventas', {
        params: { inicio: dateRange.inicio, fin: dateRange.fin },
      });
      return res.data.data as VentaItem[];
    },
    placeholderData: keepPreviousData,
    refetchInterval: 4000,
    enabled: activeReport === 'ventas',
  });

  const { data: productosStock } = useQuery({
    queryKey: ['reports', 'stock'],
    queryFn: async () => {
      const res = await api.get('/reports/stock-bajo');
      return res.data.data as ProductoItem[];
    },
    refetchInterval: 10000,
    enabled: activeReport === 'dashboard' || activeReport === 'stock',
  });

  const { data: contactosRecientes, isLoading: contactosLoading } = useQuery({
    queryKey: ['reports', 'contactos-recientes'],
    queryFn: async () => {
      const res = await api.get('/reports/contactos-recientes');
      return res.data.data as ContactoReciente[];
    },
    refetchInterval: 10000,
    enabled: activeReport === 'dashboard',
  });

  const handleExportPDF = async () => {
    try {
      setIsExportingPDF(true);
      toast.info('Generando PDF de ventas...');
      
      const pdfUrl = await exportarVentasPDF(dateRange.inicio, dateRange.fin);
      
      window.open(getFileUrl(pdfUrl), '_blank');
      
      toast.success('PDF generado exitosamente');
    } catch (error: any) {
      console.error('Error al exportar PDF:', error);
      if (error.response?.status === 404) {
        toast.warning('No hay ventas en el período seleccionado');
      } else {
        toast.error('Hubo un error al generar el PDF.');
      }
    } finally {
      setIsExportingPDF(false);
    }
  };

  const renderDashboard = () => {
    if (summaryLoading && !summary) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    const stats = [
      {
        id: 'ventas',
        label: 'Ventas Totales',
        value: formatCurrency(summary?.totalVentas || 0),
        icon: DollarSign,
        color: 'text-green-600',
        bg: 'bg-green-50',
      },
      {
        id: 'productos',
        label: 'Productos',
        value: summary?.totalProductos || 0,
        icon: Package,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
      },
      {
        id: 'clientes',
        label: 'Clientes',
        value: summary?.totalClientes || 0,
        icon: Users,
        color: 'text-purple-600',
        bg: 'bg-purple-50',
      },
      {
        id: 'stock-bajo',
        label: 'Stock Bajo',
        value: summary?.stockBajo || 0,
        icon: AlertCircle,
        color: 'text-red-600',
        bg: 'bg-red-50',
      },
    ];

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <Card key={stat.id} className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-800">{stat.value}</p>
                </div>
                <div className={`${stat.bg} p-3 rounded-full`}>
                  <stat.icon className={stat.color} size={24} />
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <UserPlus size={20} className="text-blue-500" />
              Contactos Recientes
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                Últimos {contactosRecientes?.length || 0} contactos
              </span>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                Máx. 20
              </span>
            </div>
          </div>

          {contactosLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : contactosRecientes && contactosRecientes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Contacto</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Tipo</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Teléfono</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Localidad</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Fecha de Creación</th>
                  </tr>
                </thead>
                <tbody>
                  {contactosRecientes.map((contacto) => (
                    <tr key={`${contacto.tipo}-${contacto.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {contacto.tipo === 'cliente' ? (
                            <Users size={16} className="text-blue-500" />
                          ) : (
                            <Building2 size={16} className="text-orange-500" />
                          )}
                          <span className="font-medium text-gray-800">{contacto.nombre}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          contacto.tipo === 'cliente' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {contacto.tipo === 'cliente' ? 'Cliente' : 'Proveedor'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {contacto.telefono ? (
                          <a
                            href={`tel:${contacto.telefono.replace(/\s/g, '')}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline transition-colors flex items-center gap-1"
                          >
                            <Phone size={14} className="text-blue-500" />
                            {contacto.telefono}
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {contacto.localidad ? (
                          <span className="flex items-center gap-1">
                            <MapPin size={14} className="text-gray-400" />
                            {contacto.localidad}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-500 text-sm">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} className="text-gray-400" />
                          {formatDate(contacto.fecha)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <UserPlus size={40} className="mx-auto mb-2 text-gray-300" />
              <p>No hay contactos registrados</p>
              <p className="text-sm mt-1">Los clientes y proveedores aparecerán aquí al crearlos</p>
            </div>
          )}

          <div className="mt-3 text-xs text-gray-400 border-t border-gray-100 pt-3">
            * Lista de los últimos 20 clientes y proveedores agregados
          </div>
        </Card>

        {productosStock && productosStock.length > 0 && (
          <Card className="p-6 border-red-200 bg-red-50/30">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="text-red-500" size={20} />
              <h2 className="text-lg font-semibold text-red-700">Productos con Stock Bajo</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-red-200">
                    <th className="text-left py-3 px-4 font-semibold text-red-600">Producto</th>
                    <th className="text-left py-3 px-4 font-semibold text-red-600">Categoría</th>
                    <th className="text-left py-3 px-4 font-semibold text-red-600">Stock</th>
                    <th className="text-left py-3 px-4 font-semibold text-red-600">Precio</th>
                  </tr>
                </thead>
                <tbody>
                  {productosStock.map((producto) => (
                    <tr key={producto.id} className="border-b border-red-100 hover:bg-red-50/50">
                      <td className="py-3 px-4 text-gray-700">{producto.nombre}</td>
                      <td className="py-3 px-4 text-gray-500">{producto.categoria || 'N/A'}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                          {producto.stock} unidades
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{formatCurrency(producto.precio)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {(!productosStock || productosStock.length === 0) && (
          <Card className="p-6 border-green-200 bg-green-50/30">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-500" />
              <h2 className="text-lg font-semibold text-green-700">¡Todo en orden! - No hay productos con stock bajo</h2>
            </div>
          </Card>
        )}
      </div>
    );
  };

  const renderVentas = () => {
    if (ventasLoading && !ventasRecientes) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <Input
              type="date"
              value={tempDateRange.inicio}
              onChange={(e) => setTempDateRange({ ...tempDateRange, inicio: e.target.value })}
              className="w-48"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <Input
              type="date"
              value={tempDateRange.fin}
              onChange={(e) => setTempDateRange({ ...tempDateRange, fin: e.target.value })}
              className="w-48"
            />
          </div>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => {
              setDateRange(tempDateRange);
              toast.info('Filtrando ventas...');
            }}
          >
            <Filter size={16} />
            Filtrar
          </Button>
          
          <Button 
            variant="outline" 
            onClick={handleExportPDF} 
            disabled={isExportingPDF || !ventasRecientes || ventasRecientes.length === 0}
            className="border-blue-200 text-blue-600 hover:bg-blue-50"
          >
            {isExportingPDF ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Printer size={16} />
            )}
            {isExportingPDF ? 'Generando...' : 'Exportar PDF'}
          </Button>
        </div>

        <Card className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">ID</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Cliente</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Total</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {ventasRecientes?.map((venta) => (
                  <tr key={venta.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-500">#{venta.id}</td>
                    <td className="py-3 px-4 text-gray-700">{venta.clienteNombre}</td>
                    <td className="py-3 px-4 font-medium text-gray-800">
                      {formatCurrency(venta.total)}
                    </td>
                    <td className="py-3 px-4 text-gray-500">{formatDate(venta.fechaVenta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    );
  };

  const renderStock = () => {
  if (productsLoading && !allProducts) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const productos = (allProducts?.data || []).filter((p: any) => p.activo !== false);
  const stockBajo = productos.filter((p: any) => p.stock <= 5 && p.stock > 0).length;
  const sinStock = productos.filter((p: any) => p.stock === 0).length;
  const stockOptimo = productos.filter((p: any) => p.stock > 10).length;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">Control de inventario</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <Package size={24} className="mx-auto text-blue-500 mb-2" />
          <p className="text-2xl font-bold">{stockBajo}</p>
          <p className="text-sm text-gray-500">Productos con stock bajo</p>
        </Card>
        <Card className="p-4 text-center">
          <CheckCircle size={24} className="mx-auto text-red-500 mb-2" />
          <p className="text-2xl font-bold">{sinStock}</p>
          <p className="text-sm text-gray-500">Productos sin stock</p>
        </Card>
        <Card className="p-4 text-center">
          <TrendingUp size={24} className="mx-auto text-green-500 mb-2" />
          <p className="text-2xl font-bold">{stockOptimo}</p>
          <p className="text-sm text-gray-500">Productos con stock óptimo</p>
        </Card>
      </div>

      <Card className="p-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-600">Producto</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600">Categoría</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600">Stock</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600">Precio</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-600">Estado</th>
              </tr>
            </thead>
            <tbody>
              {productos.map((producto: any) => (
                <tr key={producto.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-gray-700">{producto.nombre}</td>
                  <td className="py-3 px-4 text-gray-500">{producto.categoria || 'N/A'}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      producto.stock === 0
                        ? 'bg-red-100 text-red-700'
                        : producto.stock <= 5
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {producto.stock} unidades
                    </span>
                  </td>
                  <td className="py-3 px-4 text-gray-700">{formatCurrency(producto.precio)}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      producto.stock === 0
                        ? 'bg-red-100 text-red-700'
                        : producto.stock <= 5
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {producto.stock === 0 ? 'Sin stock' : producto.stock <= 5 ? 'Bajo' : 'Óptimo'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};

  const renderHistorial = () => {
    return <HistorialProductos />;
  };

  const renderContent = () => {
    switch (activeReport) {
      case 'dashboard':
        return renderDashboard();
      case 'ventas':
        return renderVentas();
      case 'stock':
        return renderStock();
      case 'historial':
        return renderHistorial();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Reportes</h1>
        <p className="text-sm text-gray-500">
          Visualiza y exporta análisis de tu negocio
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveReport('dashboard')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeReport === 'dashboard'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          <BarChart3 size={16} />
          Resumen
        </button>
        <button
          onClick={() => setActiveReport('ventas')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeReport === 'ventas'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          <DollarSign size={16} />
          Ventas
        </button>
        <button
          onClick={() => setActiveReport('stock')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeReport === 'stock'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          <AlertCircle size={16} />
          Stock
        </button>
        <button
          onClick={() => setActiveReport('historial')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            activeReport === 'historial'
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
          }`}
        >
          <Clock size={16} />
          Historial
        </button>
      </div>
      {renderContent()}
    </div>
  );
}